import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Button,
  Rating,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Paper,
  Chip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { formatDate } from '../../utils/helpers';
import { EventCategory, getCategoryLabel, getCategoryColor } from '../../types';
import { ROUTES } from '../../utils/constants';
import api from '../../services/api';

interface PendingEvent {
  id: string;
  title: string;
  date: string;
  category: string;
  imageUrl: string | null;
}

const FeedbackPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [pendingEvents, setPendingEvents] = useState<PendingEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<PendingEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [rating, setRating] = useState<number | null>(null);
  const [enjoyedMost, setEnjoyedMost] = useState('');
  const [improvements, setImprovements] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(true);

  const fetchPendingFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/feedback/pending');
      setPendingEvents(response.data);

      // If eventId is provided in URL, select that event
      if (eventId) {
        const event = response.data.find((e: PendingEvent) => e.id === eventId);
        if (event) {
          setSelectedEvent(event);
        }
      }
    } catch (err) {
      console.error('Failed to fetch pending feedback:', err);
      setError('Failed to load events awaiting feedback');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchPendingFeedback();
  }, [fetchPendingFeedback]);

  // Refetch when window regains focus (in case feedback was submitted elsewhere)
  useEffect(() => {
    const handleFocus = () => {
      fetchPendingFeedback();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchPendingFeedback]);

  const handleSelectEvent = (event: PendingEvent) => {
    setSelectedEvent(event);
    setError(null);
    // Reset form
    setRating(null);
    setEnjoyedMost('');
    setImprovements('');
    setAdditionalComments('');
    setWouldRecommend(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedEvent || !rating) {
      setError('Please provide a rating');
      return;
    }

    if (!enjoyedMost.trim()) {
      setError('Please tell us what you enjoyed most');
      return;
    }

    if (!improvements.trim()) {
      setError('Please provide suggestions for improvement');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await api.post('/feedback', {
        eventId: selectedEvent.id,
        rating,
        enjoyedMost: enjoyedMost.trim(),
        improvements: improvements.trim(),
        additionalComments: additionalComments.trim() || undefined,
        wouldRecommend,
      });

      setSuccess(true);
      // Remove the event from pending list
      setPendingEvents((prev) => prev.filter((e) => e.id !== selectedEvent.id));

      // Reset after a delay
      setTimeout(() => {
        setSuccess(false);
        setSelectedEvent(null);
        // If no more pending events, navigate back
        if (pendingEvents.length <= 1) {
          navigate(ROUTES.HOME);
        }
      }, 3000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Success state after submission
  if (success && selectedEvent) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Thank You!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your feedback for "{selectedEvent.title}" has been submitted.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Your input helps us improve future events.
        </Typography>
      </Box>
    );
  }

  // No pending events
  if (pendingEvents.length === 0) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          Event Feedback
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Share your thoughts on events you've attended
        </Typography>

        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <FeedbackIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No events awaiting feedback
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            After attending an event, you'll be able to submit feedback here.
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 3 }}
            onClick={() => navigate(ROUTES.EVENTS)}
          >
            Browse Events
          </Button>
        </Paper>
      </Box>
    );
  }

  // If no event selected, show list of pending events
  if (!selectedEvent) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          Event Feedback
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Select an event to share your feedback
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pendingEvents.map((event) => (
            <Card
              key={event.id}
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => handleSelectEvent(event)}
            >
              <Box sx={{ display: 'flex' }}>
                {event.imageUrl && (
                  <CardMedia
                    component="img"
                    sx={{ width: 120, height: 100, objectFit: 'cover' }}
                    image={event.imageUrl}
                    alt={event.title}
                  />
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EventIcon sx={{ color: getCategoryColor(event.category as EventCategory), fontSize: 20 }} />
                    <Typography variant="h6" fontWeight={600}>
                      {event.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={getCategoryLabel(event.category as EventCategory)}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(event.category as EventCategory),
                        color: 'white',
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(event.date)}
                    </Typography>
                  </Box>
                </CardContent>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  // Show feedback form for selected event
  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Event Feedback
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Tell us about your experience at "{selectedEvent.title}"
      </Typography>

      {pendingEvents.length > 1 && (
        <Button
          variant="text"
          onClick={() => setSelectedEvent(null)}
          sx={{ mb: 2 }}
        >
          ← Back to event list
        </Button>
      )}

      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex' }}>
          {selectedEvent.imageUrl && (
            <CardMedia
              component="img"
              sx={{ width: 150, height: 120, objectFit: 'cover' }}
              image={selectedEvent.imageUrl}
              alt={selectedEvent.title}
            />
          )}
          <CardContent>
            <Typography variant="h6" fontWeight={600}>
              {selectedEvent.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Chip
                label={getCategoryLabel(selectedEvent.category as EventCategory)}
                size="small"
                sx={{
                  backgroundColor: getCategoryColor(selectedEvent.category as EventCategory),
                  color: 'white',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {formatDate(selectedEvent.date)}
              </Typography>
            </Box>
          </CardContent>
        </Box>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Rating */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                How would you rate this event? *
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                  value={rating}
                  onChange={(_, newValue) => setRating(newValue)}
                  size="large"
                  emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                />
                {rating && (
                  <Typography variant="body2" color="text.secondary">
                    {rating === 5 && 'Excellent!'}
                    {rating === 4 && 'Great!'}
                    {rating === 3 && 'Good'}
                    {rating === 2 && 'Fair'}
                    {rating === 1 && 'Poor'}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* What they enjoyed most */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="What did you enjoy most about this event? *"
                multiline
                rows={3}
                value={enjoyedMost}
                onChange={(e) => setEnjoyedMost(e.target.value)}
                placeholder="Tell us what made this event special for you..."
              />
            </Box>

            {/* Improvements */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="How could we improve future events? *"
                multiline
                rows={3}
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
                placeholder="Your suggestions help us do better..."
              />
            </Box>

            {/* Additional comments */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Any additional comments? (optional)"
                multiline
                rows={2}
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                placeholder="Anything else you'd like to share..."
              />
            </Box>

            {/* Would recommend */}
            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={wouldRecommend}
                    onChange={(e) => setWouldRecommend(e.target.checked)}
                    color="primary"
                  />
                }
                label="I would recommend Team Jak events to other families"
              />
            </Box>

            {/* Submit button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              sx={{
                py: 1.5,
                backgroundColor: '#00b7ed',
                '&:hover': {
                  backgroundColor: '#0095c2',
                },
              }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit Feedback'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeedbackPage;
